import React, { ReactNode } from 'react';
import Icon, { IconName } from '../../Icon/Icon';
import Div from '../../Element/Div/Div';
import styles from './SideNavigationItem.module.scss';
import Flex from '../../Flex/Flex';
import BodyText from '../../Typography/BodyText/BodyText';
import Gutter from '../../Gutter/Gutter';
import SideNavigationSubItem, {
  SideNavigationSubItemType,
} from '../SideNavigationSubItem/SideNavigationSubItem';
import { CommonProps } from '../../Element/Element';

export type SideNavigationItemType = {
  /** Name of the icon */
  iconName?: IconName;
  /** Label of the navigation item */
  label: string | ReactNode;
  /** Is the item selected? */
  active: boolean;
  /** List of sub navigation item */
  subItems?: SideNavigationSubItemType[];
  /** Function that is called then user clicked */
  onClick: () => void;
  /** Is the item open? */
  isOpen?: boolean;
} & CommonProps<'div'>;

const SideNavigationItem: React.FunctionComponent<SideNavigationItemType> = ({
  iconName,
  label,
  active,
  onClick,
  subItems,
  className,
  isOpen = true,
  ...props
}) => {
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && onClick) {
      onClick();
      event.preventDefault();
    }
  };

  return (
    <>
      <Div
        className={[
          'side-navigation-test',
          styles.sideNavigationItem,
          { [styles.sideNavigationItem__active]: active, className },
        ]}
        margin={{ bottom: 'sm' }}
        hoverColor="purple500"
        padding={{ size: 'xs', left: 'sm' }}
        clickableAppearance
        tabIndex={0}
        onClick={onClick}
        onKeyDown={onKeyDown}
        {...props}
      >
        <Flex centeredVertically>
          {iconName && (
            <>
              <Icon size="md" name={iconName} />
              <Gutter size="md" />
            </>
          )}
          <BodyText tagName="span" fontWeight="medium">
            {label}
          </BodyText>

          {subItems && (
            <>
              <Div flexItem />

              <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size="md" />
            </>
          )}
        </Flex>
      </Div>

      {subItems && isOpen && (
        <Div
          margin={{ bottom: 'sm' }}
          className={[
            styles.sideNavigationItem_subItems,
            { [styles.sideNavigationItem_subItems__withoutIcon]: !iconName },
          ]}
        >
          {subItems.map(({ label, active: subActive, onClick, ...props }) => (
            <SideNavigationSubItem
              label={label}
              active={subActive}
              onClick={onClick}
              {...props}
            />
          ))}
        </Div>
      )}
    </>
  );
};

export default SideNavigationItem;
